import axios from "axios";
import { clearStaleAuthStorage } from "../utils/authStorage";
import { clearExamSessionStorage } from "../utils/examSubscription";

import {
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGIN_FAIL,
  REGISTER_USER_REQUEST,
  REGISTER_USER_SUCCESS,
  REGISTER_USER_FAIL,
  LOAD_USER_REQUEST,
  LOAD_USER_SUCCESS,
  LOAD_USER_FAIL,
  FORGOT_PASSWORD_REQUEST,
  FORGOT_PASSWORD_SUCCESS,
  FORGOT_PASSWORD_FAIL,
  FORGOT_PASSWORD_OTP_VERIFY_REQUEST,
  FORGOT_PASSWORD_OTP_VERIFY_SUCCESS,
  FORGOT_PASSWORD_OTP_VERIFY_FAIL,
  FORGOT_PASSWORD_OTP_RESEND_REQUEST,
  FORGOT_PASSWORD_OTP_RESEND_SUCCESS,
  FORGOT_PASSWORD_OTP_RESEND_FAIL,
  RESET_PASSWORD_REQUEST,
  RESET_PASSWORD_SUCCESS,
  RESET_PASSWORD_FAIL,
  LOGOUT_SUCCESS,
  LOGOUT_FAIL,
  CLEAR_ERRORS,
  NEW_REGISTER_REQUEST,
  NEW_REGISTER_SUCCESS,
  NEW_REGISTER_FAIL,
  OTP_VERIFY_REQUEST,
  OTP_VERIFY_SUCCESS,
  OTP_VERIFY_FAIL,
  OTP_RESEND_REQUEST,
  OTP_RESEND_SUCCESS,
  OTP_RESEND_FAIL,
} from "../constants/authConstants";

// Normalize API base (avoid trailing slash issues)
const __raw_api_path = process.env.REACT_APP_API_BASE_URL || "/api/v1";
const _api_path = __raw_api_path.replace(/\/+$/, "");

// New landing-style registration endpoints (expects backend compatibility)
const NEW_REG_ENDPOINT = `${_api_path}/newregistration`;
const VERIFY_OTP_ENDPOINT = `${_api_path}/verify-otp`;
const RESEND_OTP_ENDPOINT = `${_api_path}/resend-otp`;

//login
export const login = (email, password) => async (dispatch) => {
  try {
    dispatch({
      type: LOGIN_REQUEST,
    });
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    };

    const { data } = await axios.post(
      _api_path + "/login",
      { email, password },
      config
    );
    if (!data) {
      dispatch({
        type: LOGIN_FAIL,
        payload: "Invalid email or password",
      });
    }
    
    // Token is now in HttpOnly cookie (set by backend) — no localStorage needed
    
    dispatch({
      type: LOGIN_SUCCESS,
      payload: data.user,
    });

    // Immediately load the full user profile from /me to ensure
    // roleId is populated with permissions for the admin panel
    dispatch(loadUser());
  } catch (error) {
    // console.log('error.response ', error.response); return;
    const payload =
      error?.response?.data?.errMessage ||
      error?.response?.data?.message ||
      "Login failed";
    dispatch({
      type: LOGIN_FAIL,
      payload,
      errors: error?.response?.data,
    });
  }
};

export const listSchools = async () => {
  const { data } = await axios.get(`${_api_path}/schools/search`, {
    withCredentials: true,
    params: { limit: 100 },
  });
  return data?.schools || [];
};

export const searchSchools = async (query) => {
  const config = {
    withCredentials: true,
  };
  const { data } = await axios.get(`${_api_path}/schools/search`, {
    ...config,
    params: { q: query, limit: 10 },
  });
  return data?.schools || [];
};

export const loginWithSchoolStudent = (schoolCode, studentId) => async (dispatch) => {
  try {
    dispatch({ type: LOGIN_REQUEST });

    const config = {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    };

    const { data } = await axios.post(
      `${_api_path}/schools/students/login`,
      { schoolCode, studentId },
      config
    );

    if (!data?.user) {
      dispatch({
        type: LOGIN_FAIL,
        payload: "Unable to log in with this student ID",
      });
      return;
    }

    clearExamSessionStorage();

    dispatch({
      type: LOGIN_SUCCESS,
      payload: data.user,
    });

    dispatch(loadUser());
    return data;
  } catch (error) {
    const payload =
      error?.response?.data?.message ||
      error?.response?.data?.errMessage ||
      "Unable to log in with this student ID";
    dispatch({
      type: LOGIN_FAIL,
      payload,
      errors: error?.response?.data?.errors || error?.response?.data,
    });
  }
};

//Register User
export const register =
  (
    first_name,
    last_name,
    email,
    dob,
    gender,
    password,
    confirm_password,
    mobile_number,
    mobile_code,
    nationality
  ) =>
  async (dispatch) => {
    try {
      dispatch({
        type: REGISTER_USER_REQUEST,
      });
      const config = {
        headers: {
          //"content-type": "multipart/form-data",
          "content-type": "application/json",
        },
      };
      // console.log("userData ", userData.getAll());
      // return;
      const { data } = await axios.post(
        _api_path + "/register",
        // userData,
        {
          first_name,
          last_name,
          email,
          dob,
          gender,
          password,
          confirm_password,
          mobile_number,
          mobile_code,
          nationality,
        },
        config
      );
      // console.log('data', data); return;
      /*if (!data) {
          dispatch({
            type: REGISTER_USER_FAIL,
            payload: "Invalid email or password",
          });
        }*/
      dispatch({
        type: REGISTER_USER_SUCCESS,
        payload: data.user,
      });
    } catch (error) {
      //console.log("error.response", error.response.error);
      var errorMsg = error.response.data.errMessage;
      if (
        error.response.data.error !== undefined &&
        error.response.data.error.code === 11000
      ) {
        errorMsg = "Entered email already exist, please try another.";
      }
      dispatch({
        type: REGISTER_USER_FAIL,
        payload: errorMsg,
        errors: error.response.data,
      });
    }
  };

//Load user
export const loadUser = (e) => async (dispatch) => {
  try {
    dispatch({
      type: LOAD_USER_REQUEST,
    });

    // Token is sent automatically as HttpOnly cookie via withCredentials
    const config = {
      withCredentials: true,
      skipAuthRedirect: true,
    };

    const { data } = await axios.get(_api_path + "/me", config);

    dispatch({
      type: LOAD_USER_SUCCESS,
      payload: data.user,
    });
  } catch (error) {
    console.error("Error loading user:", error);
    clearStaleAuthStorage();
    dispatch({
      type: LOAD_USER_FAIL,
      payload: error.response?.data?.errMessage || "Session expired",
    });
  }
};

//admin Forgot Password
export const adminForgotPassword = (email) => async (dispatch) => {
  try {
    dispatch({
      type: FORGOT_PASSWORD_REQUEST,
    });

    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    const { data } = await axios.post(
      _api_path + "/admin/forgot-password",
      { email },
      config
    );

    dispatch({
      type: FORGOT_PASSWORD_SUCCESS,
      payload: data.message,
    });
  } catch (error) {
    dispatch({
      type: FORGOT_PASSWORD_FAIL,
      payload: error.response.data.errMessage,
      errors: error.response.data,
    });
  }
};

//Forgot Password — now sends OTP
export const forgotPassword = (email) => async (dispatch) => {
  try {
    dispatch({
      type: FORGOT_PASSWORD_REQUEST,
    });

    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    const { data } = await axios.post(
      _api_path + "/password/forgot",
      { email },
      config
    );

    dispatch({
      type: FORGOT_PASSWORD_SUCCESS,
      payload: { message: data.message, email: data.data?.email || email },
    });
  } catch (error) {
    dispatch({
      type: FORGOT_PASSWORD_FAIL,
      payload: error.response.data.errMessage,
      errors: error.response.data,
    });
  }
};

// Verify OTP for password reset
export const verifyPasswordResetOtp = (email, otp) => async (dispatch) => {
  try {
    dispatch({ type: FORGOT_PASSWORD_OTP_VERIFY_REQUEST });

    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await axios.post(
      VERIFY_OTP_ENDPOINT,
      { email, otp, purpose: "password_reset" },
      config
    );

    if (!data.success) {
      return dispatch({
        type: FORGOT_PASSWORD_OTP_VERIFY_FAIL,
        payload: data.message || "OTP verification failed",
      });
    }

    dispatch({
      type: FORGOT_PASSWORD_OTP_VERIFY_SUCCESS,
      payload: {
        message: data.message,
        token: data.data?.token || data.data?.passwordResetToken,
      },
    });
  } catch (error) {
    dispatch({
      type: FORGOT_PASSWORD_OTP_VERIFY_FAIL,
      payload: error.response?.data?.message || error.message,
    });
  }
};

// Resend OTP for password reset
export const resendPasswordResetOtp = (email) => async (dispatch) => {
  try {
    dispatch({ type: FORGOT_PASSWORD_OTP_RESEND_REQUEST });

    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await axios.post(
      RESEND_OTP_ENDPOINT,
      { email, purpose: "password_reset" },
      config
    );

    if (!data.success) {
      return dispatch({
        type: FORGOT_PASSWORD_OTP_RESEND_FAIL,
        payload: data.message || "Unable to resend OTP",
      });
    }

    dispatch({
      type: FORGOT_PASSWORD_OTP_RESEND_SUCCESS,
      payload: { message: data.message },
    });
  } catch (error) {
    dispatch({
      type: FORGOT_PASSWORD_OTP_RESEND_FAIL,
      payload: error.response?.data?.message || error.message,
    });
  }
};

//Reset / New Password
export const resetPassword =
  (token, password, confirm_password) => async (dispatch) => {
    try {
      dispatch({
        type: RESET_PASSWORD_REQUEST,
      });

      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };

      const { data } = await axios.put(
        _api_path + `/password/reset/${token}`,
        { password, confirm_password },
        config
      );

      dispatch({
        type: RESET_PASSWORD_SUCCESS,
        payload: data.success,
      });
    } catch (error) {
      dispatch({
        type: RESET_PASSWORD_FAIL,
        payload: error.response.data.errMessage,
        errors: error.response.data,
      });
    }
  };

//log out
export const logoutUser = () => async (dispatch) => {
  try {
    await axios.post(_api_path + "/logout", {}, { withCredentials: true });
    clearStaleAuthStorage();
    dispatch({
      type: LOGOUT_SUCCESS,
      payload: "logout",
    });
  } catch (err) {
    clearStaleAuthStorage();
    dispatch({
      type: LOGOUT_FAIL,
      payload: err.response?.data?.errMessage || "Logout failed",
    });
  }
};

// ---------------------------------------------------------------------------
// New Registration Flow (Parity with Landing Page)
// Steps: newRegister -> verifyOtp -> (optional) resendOtp
// ---------------------------------------------------------------------------

export const newRegister = (first_name, last_name, email, mobile_code, mobile_number, password, confirm_password) => async (dispatch) => {
  try {
    dispatch({ type: NEW_REGISTER_REQUEST });
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await axios.post(NEW_REG_ENDPOINT, {
      first_name,
      last_name,
      email,
      mobile_code,
      mobile_number,
      password,
      confirm_password,
    }, config);

    if (!data.success) {
      return dispatch({
        type: NEW_REGISTER_FAIL,
        payload: data.message || "Registration failed",
        errors: data,
      });
    }

    dispatch({
      type: NEW_REGISTER_SUCCESS,
      payload: { message: data.message, email }, // store email for OTP step
    });
  } catch (error) {
    dispatch({
      type: NEW_REGISTER_FAIL,
      payload: error.response?.data?.message || error.message,
      errors: error.response?.data,
    });
  }
};

export const verifyOtp = (email, otp) => async (dispatch) => {
  try {
    dispatch({ type: OTP_VERIFY_REQUEST });
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await axios.post(VERIFY_OTP_ENDPOINT, { email, otp }, config);

    if (!data.success) {
      return dispatch({
        type: OTP_VERIFY_FAIL,
        payload: data.message || "OTP verification failed",
        errors: data,
      });
    }

    dispatch({
      type: OTP_VERIFY_SUCCESS,
      payload: { message: data.message, email },
    });
  } catch (error) {
    dispatch({
      type: OTP_VERIFY_FAIL,
      payload: error.response?.data?.message || error.message,
      errors: error.response?.data,
    });
  }
};

export const resendOtp = (email) => async (dispatch) => {
  try {
    dispatch({ type: OTP_RESEND_REQUEST });
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await axios.post(RESEND_OTP_ENDPOINT, { email }, config);

    if (!data.success) {
      return dispatch({
        type: OTP_RESEND_FAIL,
        payload: data.message || "Unable to resend OTP",
        errors: data,
      });
    }

    dispatch({
      type: OTP_RESEND_SUCCESS,
      payload: { message: data.message, email },
    });
  } catch (error) {
    dispatch({
      type: OTP_RESEND_FAIL,
      payload: error.response?.data?.message || error.message,
      errors: error.response?.data,
    });
  }
};

//clear errors
export const clearErrors = () => async (dispatch) => {
  dispatch({
    type: CLEAR_ERRORS,
  });
};
